import React from 'react';
import IRRBarChart from '../IRRBarChart';
import IRRLineChart from '../IRRLineChart';
import MultiIRRBarChart from '../MultiIRRBarChart';
import MultiIRRLineChart from '../MultiIRRLineChart';
import type { InvestorAllocationsResponse } from '../../models/InvestmentEntity';
import type IRRDataPoint from '../../models/IRRDataPoint';
import {
  buildEntityGroupsForChart,
  buildMultiChartData,
  type FundUpdateChartType,
} from '../../pages/fundUpdate/fundUpdateUtils';

export interface FundUpdateChartsProps {
  chartType: FundUpdateChartType;
  chartData: IRRDataPoint[];
  combinedEntityView: boolean;
  allocationsData?: InvestorAllocationsResponse;
  selectedEntityIds: Set<string>;
  fundSize?: number;
  investmentKey: number;
  /** Admin's Fund Management preview uses the app's actual button colours instead of the investor chart's dedicated graph-line colours. */
  canEdit?: boolean;
}

const FundUpdateCharts: React.FC<FundUpdateChartsProps> = ({
  chartType,
  chartData,
  combinedEntityView,
  allocationsData,
  selectedEntityIds,
  fundSize,
  investmentKey,
  canEdit = false,
}) => {
  if (!combinedEntityView && allocationsData && selectedEntityIds.size > 0) {
    const entityGroups = buildEntityGroupsForChart(allocationsData.allocations, selectedEntityIds);

    if (entityGroups.size > 0) {
      const seriesKeys = Array.from(entityGroups.keys()).map((id) => entityGroups.get(id)!.name);
      const multiChartData = buildMultiChartData(chartData, entityGroups, fundSize || 1, chartType);

      return chartType === 'Cumulative' ? (
        <MultiIRRLineChart
          data={multiChartData as React.ComponentProps<typeof MultiIRRLineChart>['data']}
          seriesKeys={seriesKeys}
          tone="default"
        />
      ) : (
        <MultiIRRBarChart
          data={multiChartData as React.ComponentProps<typeof MultiIRRBarChart>['data']}
          seriesKeys={seriesKeys}
          tone="default"
        />
      );
    }
  }

  return chartType === 'Cumulative' ? (
    <IRRLineChart data={chartData} tone="default" canEdit={canEdit} key={`line-${investmentKey}`} />
  ) : (
    <IRRBarChart data={chartData} tone="default" canEdit={canEdit} key={`bar-${investmentKey}`} />
  );
};

export default FundUpdateCharts;
